// The boat's UniFi Protect key in Settings (TOM-835, cloud ADR 0412; contract
// §5.26): the view the server returns, and every sentence the section says
// about it.
//
// The key is write-only. Nothing here holds it: the server returns its last
// four characters at most, and the form's field is never filled from the
// server.
//
// Pure, so cloud/web's Node vitest covers it.

export type ProtectStatus = 'none' | 'waiting' | 'applied' | 'rejected' | 'set_on_boat';

export type ProtectConnection =
  | 'connected'
  | 'connecting'
  | 'unreachable'
  | 'unauthorized'
  | 'cert_unconfirmed'
  | 'cert_changed'
  | 'not_configured';

export interface ProtectCertificate {
  observedSha256: string | null;
  observedValidTo: string | null;
  pinnedSha256: string | null;
  pinnedValidTo: string | null;
}

export interface ProtectView {
  configured: boolean;
  host: string | null;
  keyLast4: string | null;
  version: number | null;
  status: ProtectStatus;
  reason: string | null;
  setOnBoatAt: string | null;
  connection: ProtectConnection | null;
  certificate: ProtectCertificate | null;
  pinConfirm: {
    phase: 'waiting' | 'applied' | 'rejected';
    reason: string | null;
    at: string;
  } | null;
  stateAt: string | null;
  /** The core version the boat needs before it can take a key, or null once it can. */
  needsCoreVersion: string | null;
}

/** How often the section asks again while the boat has not answered. */
export const PROTECT_POLL_MS = 5_000;

const STATUSES: readonly ProtectStatus[] = [
  'none',
  'waiting',
  'applied',
  'rejected',
  'set_on_boat',
];

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/** The server's view, or null when the reply is not one. */
export function parseProtectView(raw: unknown): ProtectView | null {
  if (raw === null || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  if (!STATUSES.includes(v.status as ProtectStatus)) return null;
  const cert =
    v.certificate !== null && typeof v.certificate === 'object'
      ? (v.certificate as Record<string, unknown>)
      : null;
  const pin =
    v.pinConfirm !== null && typeof v.pinConfirm === 'object'
      ? (v.pinConfirm as Record<string, unknown>)
      : null;
  return {
    configured: v.configured === true,
    host: str(v.host),
    keyLast4: str(v.keyLast4),
    version: typeof v.version === 'number' ? v.version : null,
    status: v.status as ProtectStatus,
    reason: str(v.reason),
    setOnBoatAt: str(v.setOnBoatAt),
    connection: (str(v.connection) as ProtectConnection | null) ?? null,
    certificate:
      cert === null
        ? null
        : {
            observedSha256: str(cert.observedSha256),
            observedValidTo: str(cert.observedValidTo),
            pinnedSha256: str(cert.pinnedSha256),
            pinnedValidTo: str(cert.pinnedValidTo),
          },
    pinConfirm:
      pin === null || typeof pin.at !== 'string'
        ? null
        : {
            phase:
              pin.phase === 'applied' || pin.phase === 'rejected'
                ? pin.phase
                : ('waiting' as const),
            reason: str(pin.reason),
            at: pin.at,
          },
    stateAt: str(v.stateAt),
    needsCoreVersion: str(v.needsCoreVersion),
  };
}

/** A boat's refusal, in plain words. */
export function protectRejectText(reason: string | null): string {
  switch (reason) {
    case 'invalid_command':
      return 'the boat could not read the key. Check it and save it again.';
    case 'not_configured_host':
      return 'the boat has no console yet. Enter the console’s address with the key.';
    case 'host_not_local':
      return 'that address is not on the boat’s own network.';
    case 'superseded_local':
      return 'a newer key was set on the boat itself.';
    case 'cert_changed':
      return 'the console’s certificate changed. Check the new one below.';
    case 'secret_store_unavailable':
      return 'the boat could not store the key. Try again later.';
    case 'unreachable':
      return 'the boat couldn’t reach the console. Try again.';
    default:
      return 'the boat did not take the key.';
  }
}

/** A time as the owner reads it, in this browser's own zone. */
export function protectTime(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

/** The one status line, or null when nothing is set and the boat has said nothing. */
export function protectStatusLine(view: ProtectView, locale?: string): string | null {
  switch (view.status) {
    case 'waiting':
      // A boat too old to take a key would keep this waiting for ever.
      return view.needsCoreVersion !== null
        ? `Needs Neomaris Core ${view.needsCoreVersion} or later on the boat. The key is kept and sent when it updates.`
        : 'Waiting for the boat…';
    case 'rejected':
      return `Rejected: ${protectRejectText(view.reason)}`;
    case 'set_on_boat':
      return view.setOnBoatAt !== null
        ? `Set on the boat at ${protectTime(view.setOnBoatAt, locale)}`
        : 'Set on the boat';
    case 'applied':
      if (!view.configured) return 'Removed from the boat';
      return connectionLine(view.connection) ?? 'The boat has the key';
    case 'none':
      return connectionLine(view.connection);
  }
}

function connectionLine(connection: ProtectConnection | null): string | null {
  switch (connection) {
    case 'connected':
      return 'Connected';
    case 'connecting':
      return 'Connecting…';
    case 'unreachable':
      return 'The boat cannot reach the console';
    case 'unauthorized':
      return 'The console refused the key';
    case 'cert_unconfirmed':
      return 'Confirm the console’s certificate';
    case 'cert_changed':
      return 'The console’s certificate changed';
    default:
      return null;
  }
}

/** Whether the certificate step shows: the boat waits for an owner to confirm one. */
export function needsCertificateConfirm(view: ProtectView): boolean {
  return (
    (view.connection === 'cert_unconfirmed' || view.connection === 'cert_changed') &&
    view.certificate?.observedSha256 != null
  );
}

/** Whether the section asks again in a moment. */
export function shouldPollProtect(view: ProtectView | null): boolean {
  if (view === null) return false;
  return (
    view.status === 'waiting' ||
    view.connection === 'connecting' ||
    view.pinConfirm?.phase === 'waiting'
  );
}

/** A SHA-256 as four lines of eight pairs, `D6:A4:…`, to read against the console. */
export function groupFingerprint(hex: string): string[] {
  const pairs = hex.toUpperCase().match(/[0-9A-F]{2}/g) ?? [];
  const lines: string[] = [];
  for (let i = 0; i < pairs.length; i += 8) lines.push(pairs.slice(i, i + 8).join(':'));
  return lines;
}

/** The line under the Confirm button once a confirm was sent. */
export function pinConfirmText(pin: ProtectView['pinConfirm']): string | null {
  if (pin === null) return null;
  if (pin.phase === 'waiting') return 'Sending your confirmation to the boat…';
  if (pin.phase === 'applied') return 'Confirmed. The boat trusts this certificate.';
  if (pin.reason === 'cert_changed') {
    return 'The console now presents a different certificate. Check the new one and confirm again.';
  }
  return 'The boat did not take the confirmation. Try again.';
}

/** The sentence for a refused save, removal or confirm. Never the server's own words. */
export function protectErrorMessage(status: number, body: unknown): string {
  const code =
    body !== null &&
    typeof body === 'object' &&
    typeof (body as { error?: unknown }).error === 'string'
      ? (body as { error: string }).error
      : null;
  if (code === 'certificate_not_presented') {
    return 'The console presents a different certificate now. Reload and check again.';
  }
  if (code === 'nothing_to_confirm') return 'The boat has no certificate waiting to be confirmed.';
  if (code === 'boat_offline') {
    return 'The boat is offline. A certificate can be confirmed only while it is online.';
  }
  if (status === 400) {
    return 'Check the key: 16 to 256 characters with no spaces, and the address with no spaces.';
  }
  if (status === 401) return 'Sign in again, then try again.';
  if (status === 403) return 'Reload the page, then try again.';
  if (status === 404) return 'This boat is no longer on your account.';
  if (status === 429) return 'Too many changes at once. Try again in a minute.';
  return 'That didn’t save. Try again.';
}
